// @ts-strict-ignore
import React, { useCallback, useEffect, useState } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import {
  Button,
  Box as CenteredWrapper,
  Box as Form,
  Input,
  QRCode,
  Box as Wrapper,
} from '@node-space/ui-components'
import { logError } from '@node-space/utils'
import TwoFAField, { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { PathNames } from 'constants/General'
import { useTwoFactorCodeMutation } from 'hooks/mutations/useTwoFactorCodeMutation'
import { useGetOtpUrl } from 'hooks/queries/useGetOtpUrlQuery'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { BaseErrorResponse } from 'types/beneficiaries'
import Description from './components/Description'
import { twoFaSchema } from './schema'

const TwoFactorAuthMandatory = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setIsTwoFactorEnabled } = useTwoFAContext()

  const [enter2FaStep, setEnter2FaStep] = useState(false)
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const [twoFactorCodeError, setTwoFactorCodeError] = useState('')
  const { isPending: isFetchingCodeResponse, mutate } = useTwoFactorCodeMutation()
  const {
    register,
    formState: { errors, isValid },
    control,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
  } = useForm<{
    password: string
  }>({
    defaultValues: {
      password: '',
    },
    resolver: yupFormResolver<{ password: string }>(twoFaSchema(t)),
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const formValues = useWatch({ control })

  const INVALID_CODE_RESPONSE = 412

  const { secret, otpUrl, isFetchingSecrect, isErrorGetSecrectCall, refetchSecrect } = useGetOtpUrl(
    formValues?.password
  )

  useEffect(() => {
    if (!isFetchingSecrect) {
      if (secret === null) {
        setError('password', { type: 'invalidPassword', message: t('incorrectPassword') })
      } else if (secret) {
        setEnter2FaStep(true)
      }

      if (isErrorGetSecrectCall) {
        setError('password', { type: 'genericError', message: t('oopsSomethingWentWrong') })
      }
    }
  }, [isFetchingSecrect, secret, isErrorGetSecrectCall])

  const onSubmit: SubmitHandler<{
    password: string
  }> = async () => {
    clearErrors()
    setTwoFactorCodeError('')

    if (!enter2FaStep) {
      await refetchSecrect()
    } else {
      mutate(twoFactorCode, {
        onSuccess: () => {
          setIsTwoFactorEnabled(true)
        },
        onError: (error: BaseErrorResponse) => {
          let errMsg = ''

          if (error?.status === INVALID_CODE_RESPONSE) {
            errMsg = t('invalidCode')
          } else {
            errMsg = t('oopsSomethingWentWrong')
            logError('Error from TwoFactorAuthMandatory - onSubmit', error)
          }
          setTwoFactorCodeError(errMsg)
        },
      })
    }
  }

  const handleTwoFactorCodeChange = useCallback(
    (value: string) => {
      setTwoFactorCode(value)

      if (twoFactorCodeError) {
        setTwoFactorCodeError('')
      }
    },
    [twoFactorCodeError, setTwoFactorCodeError]
  )

  const isSubmitCodeValid = twoFactorCode.length < TWO_FA_REQUIRED_LENGTH

  return (
    <Wrapper flex justify="center" height="screen">
      <CenteredWrapper flexCol className="max-w-lg" centerChildren>
        <h3 className="text-h3 text-center text-foreground-prominent mb-4 w-2/3">
          {t('twoFactorVerification.title')}
        </h3>

        {enter2FaStep && (
          <Wrapper
            flex
            justify="center"
            background="standard"
            borderRadius={8}
            padding={12}
            className="mb-3"
          >
            <QRCode value={otpUrl} />
          </Wrapper>
        )}
        <p className="mb-4 text-foreground-prominent text-body-regular-small">{secret}</p>
        <Description />

        {enter2FaStep && (
          <p className="text-body-label text-foreground-prominent">
            {t('twoFactorVerification.enter2FaCode')}
          </p>
        )}

        <Form
          tag="form"
          width="full"
          paddingT={4}
          testId="2fa-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          {!enter2FaStep && (
            <Input
              {...register('password')}
              testId="password-2FA-input"
              label={t('twoFactorVerification.enterPassword')}
              type="password"
              placeholder={t('enterPassword')}
              error={Boolean(errors?.password)}
              errorText={errors?.password?.message}
              disabled={isFetchingSecrect}
              LabelButton={
                <Link to={PathNames.FORGOT_PASSWORD}>
                  <Button variant="ghost" noPadding>
                    {t('forgotPassword')}
                  </Button>
                </Link>
              }
              onChange={({ target: { value } }) => {
                setValue('password', value, { shouldValidate: true })
              }}
            />
          )}

          {enter2FaStep && (
            <TwoFAField
              error={twoFactorCodeError}
              isLoading={isFetchingCodeResponse}
              onChange={handleTwoFactorCodeChange}
            />
          )}
          <Wrapper flex justify="center" paddingT={24} width="full" gap={16}>
            <Button
              variant="secondary"
              disabled={isFetchingSecrect || isFetchingCodeResponse}
              onClick={() => navigate(PathNames.LOGOUT)}
            >
              {t('twoFactorVerification.logoutButton')}
            </Button>
            {!enter2FaStep && (
              <Button loading={isFetchingSecrect} disabled={!isValid} type="submit">
                {t('continue')}
              </Button>
            )}
            {enter2FaStep && (
              <Button type="submit" loading={isFetchingCodeResponse} disabled={isSubmitCodeValid}>
                {t('confirm')}
              </Button>
            )}
          </Wrapper>
        </Form>
      </CenteredWrapper>
    </Wrapper>
  )
}

export default TwoFactorAuthMandatory
